import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Row, Col, Container, Card } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import {
  createDeliveryProfile,
  updateDeliveryProfile,
  fetchDeliveryProfileById,
  clearSuccess,
} from "../../redux/slices/deliveryProfileSlice";
import { toast } from "react-toastify";
import { FaRegTrashAlt, FaCalendarAlt } from "react-icons/fa";
import { Country, State, City } from "country-state-city";

// Helper function to convert YYYY-MM-DD to DD-MM-YYYY (for display and backend)
const convertYYYYMMDDToDDMMYYYY = (dateString) => {
  if (!dateString) return "";

  // Check if already in DD-MM-YYYY format
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
    return dateString;
  }

  // Check if in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  }

  // Check if in DD/MM/YYYY format (backward compatibility)
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
    return dateString.replace(/\//g, "-");
  }

  // Try to parse as Date and convert (avoid timezone issues)
  const date = new Date(dateString);
  if (!isNaN(date.getTime())) {
    // Use local date components to avoid timezone shift
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  return dateString;
};

// Helper function to convert DD-MM-YYYY to YYYY-MM-DD (for date input)
const convertDDMMYYYYToYYYYMMDD = (dateString) => {
  if (!dateString) return "";

  // Check if already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  // Check if in DD-MM-YYYY format
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
    const [day, month, year] = dateString.split("-");
    return `${year}-${month}-${day}`;
  }

  // Check if in DD/MM/YYYY format (backward compatibility)
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
    const [day, month, year] = dateString.split("/");
    return `${year}-${month}-${day}`;
  }

  return "";
};

// Helper function to format date input as user types (DD-MM-YYYY)
const formatDateInput = (value) => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, "");

  // Limit to 8 digits (DDMMYYYY)
  const limitedDigits = digits.slice(0, 8);

  // Format as DD-MM-YYYY
  if (limitedDigits.length <= 2) {
    return limitedDigits;
  } else if (limitedDigits.length <= 4) {
    return `${limitedDigits.slice(0, 2)}-${limitedDigits.slice(2)}`;
  } else {
    return `${limitedDigits.slice(0, 2)}-${limitedDigits.slice(
      2,
      4
    )}-${limitedDigits.slice(4)}`;
  }
};

// Helper function to validate DD-MM-YYYY date format
const validateDate = (dateString) => {
  if (!dateString) return false;

  const datePattern = /^(\d{2})-(\d{2})-(\d{4})$/;
  const match = dateString.match(datePattern);

  if (!match) return false;

  const [, day, month, year] = match;
  const dayNum = parseInt(day, 10);
  const monthNum = parseInt(month, 10);
  const yearNum = parseInt(year, 10);

  // Basic validation
  if (
    dayNum < 1 ||
    dayNum > 31 ||
    monthNum < 1 ||
    monthNum > 12 ||
    yearNum < 1900 ||
    yearNum > 2100
  ) {
    return false;
  }

  // Validate actual date
  const date = new Date(yearNum, monthNum - 1, dayNum);
  return (
    date.getFullYear() === yearNum &&
    date.getMonth() + 1 === monthNum &&
    date.getDate() === dayNum
  );
};

const DeliveryProfileForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isEditMode = !!id;

  const {
    isCreating,
    isUpdating,
    isLoading,
    error,
    success,
    currentDeliveryProfile,
  } = useSelector((state) => state.deliveryProfiles);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    gender: "",
    email: "",
    phone: "",
    address: "",
    country: "",
    state: "",
    city: "",
    pincode: "",
    aadhar_card: "",
    pan_card: "",
    driving_licence: "",
    image_url: "",
    hire_date: "",
    is_active: true,
  });

  const [imageFile, setImageFile] = useState(null);
  const [aadharFile, setAadharFile] = useState(null);
  const [panFile, setPanFile] = useState(null);
  const [drivingLicenceFile, setDrivingLicenceFile] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const dateInputRef = React.useRef(null);

  // Country, State, City dropdowns
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const [selectedStateCode, setSelectedStateCode] = useState("");

  // Get all countries
  const countries = Country.getAllCountries();

  // Get states based on selected country
  const states = selectedCountryCode
    ? State.getStatesOfCountry(selectedCountryCode)
    : [];

  // Get cities based on selected country and state
  const cities =
    selectedCountryCode && selectedStateCode
      ? City.getCitiesOfState(selectedCountryCode, selectedStateCode)
      : [];

  // Load delivery profile data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchDeliveryProfileById(id));
    }
  }, [dispatch, id, isEditMode]);

  // Update form data when currentDeliveryProfile changes
  useEffect(() => {
    if (isEditMode && currentDeliveryProfile) {
      const country = currentDeliveryProfile.country || "";
      const state = currentDeliveryProfile.state || "";

      setFormData({
        first_name: currentDeliveryProfile.first_name || "",
        last_name: currentDeliveryProfile.last_name || "",
        gender: currentDeliveryProfile.gender || "",
        email: currentDeliveryProfile.email || "",
        phone: currentDeliveryProfile.phone || "",
        address: currentDeliveryProfile.address || "",
        country: country,
        state: state,
        city: currentDeliveryProfile.city || "",
        pincode: currentDeliveryProfile.pincode || "",
        aadhar_card: currentDeliveryProfile.aadhar_card || "",
        pan_card: currentDeliveryProfile.pan_card || "",
        driving_licence: currentDeliveryProfile.driving_licence || "",
        image_url: currentDeliveryProfile.image_url || "",
        hire_date:
          convertYYYYMMDDToDDMMYYYY(currentDeliveryProfile.hire_date) || "",
        is_active:
          currentDeliveryProfile.is_active !== undefined
            ? currentDeliveryProfile.is_active
            : true,
      });

      // Set country and state codes for dropdowns
      if (country) {
        // Find country by name
        const foundCountry = countries.find(
          (c) => c.name === country || c.isoCode === country
        );
        if (foundCountry) {
          setSelectedCountryCode(foundCountry.isoCode);

          // Find state by name
          if (state && foundCountry.isoCode) {
            const countryStates = State.getStatesOfCountry(
              foundCountry.isoCode
            );
            const foundState = countryStates.find(
              (s) => s.name === state || s.isoCode === state
            );
            if (foundState) {
              setSelectedStateCode(foundState.isoCode);
            }
          }
        }
      }
    }
  }, [currentDeliveryProfile, isEditMode, countries]);

  // Handle toast messages
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      toast.success(success);
      const timer = setTimeout(() => {
        dispatch(clearSuccess());
        navigate("/admin/delivery-profiles");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, navigate, dispatch]);

  // Cleanup effect to clear success message when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearSuccess());
    };
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Handle boolean values for select fields
    let processedValue = value;
    if (name === "is_active") {
      processedValue = value === "true" || value === true;
    }

    // Handle pincode - only allow digits and limit to 6 characters
    if (name === "pincode") {
      // Remove any non-digit characters and limit to 6 digits
      const digitsOnly = value.replace(/\D/g, "").slice(0, 6);
      processedValue = digitsOnly;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle country selection
  const handleCountryChange = (e) => {
    const countryCode = e.target.value;
    const selectedCountry = countries.find((c) => c.isoCode === countryCode);

    setSelectedCountryCode(countryCode);
    setSelectedStateCode(""); // Reset state when country changes
    setFormData((prev) => ({
      ...prev,
      country: selectedCountry ? selectedCountry.name : "",
      state: "", // Reset state
      city: "", // Reset city
    }));
  };

  // Handle state selection
  const handleStateChange = (e) => {
    const stateCode = e.target.value;
    const selectedState = states.find((s) => s.isoCode === stateCode);

    setSelectedStateCode(stateCode);
    setFormData((prev) => ({
      ...prev,
      state: selectedState ? selectedState.name : "",
      city: "", // Reset city when state changes
    }));
  };

  // Handle city selection
  const handleCityChange = (e) => {
    const cityName = e.target.value;
    setFormData((prev) => ({
      ...prev,
      city: cityName,
    }));
  };

  // File selection handlers
  const handleImageSelect = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      switch (fileType) {
        case "profile":
          setImageFile(file);
          break;
        case "aadhar":
          setAadharFile(file);
          break;
        case "pan":
          setPanFile(file);
          break;
        case "driving_licence":
          setDrivingLicenceFile(file);
          break;
        default:
          break;
      }
    } else {
      switch (fileType) {
        case "profile":
          setImageFile(null);
          break;
        case "aadhar":
          setAadharFile(null);
          break;
        case "pan":
          setPanFile(null);
          break;
        case "driving_licence":
          setDrivingLicenceFile(null);
          break;
        default:
          break;
      }
    }
  };

  // Remove file handlers
  const handleRemoveFile = (fileType) => {
    switch (fileType) {
      case "profile":
        setImageFile(null);
        if (isEditMode) {
          setFormData((prev) => ({ ...prev, image_url: "" }));
        }
        break;
      case "aadhar":
        setAadharFile(null);
        if (isEditMode) {
          setFormData((prev) => ({ ...prev, aadhar_card: "" }));
        }
        break;
      case "pan":
        setPanFile(null);
        if (isEditMode) {
          setFormData((prev) => ({ ...prev, pan_card: "" }));
        }
        break;
      case "driving_licence":
        setDrivingLicenceFile(null);
        if (isEditMode) {
          setFormData((prev) => ({ ...prev, driving_licence: "" }));
        }
        break;
      default:
        break;
    }
  };

  const validateForm = () => {
    const errors = {};

    // Required fields validation
    if (!formData.first_name.trim()) {
      errors.first_name = "First name is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid";
    }

    if (!formData.last_name.trim()) {
      errors.last_name = "Last name is required";
    }

    if (!formData.gender) {
      errors.gender = "Gender is required";
    }

    if (!formData.phone.trim()) {
      errors.phone = "Phone number is required";
    }

    if (!formData.address.trim()) {
      errors.address = "Address is required";
    }

    if (!formData.country.trim()) {
      errors.country = "Country is required";
    }

    if (!formData.state.trim()) {
      errors.state = "State is required";
    }

    if (!formData.city.trim()) {
      errors.city = "City is required";
    }

    if (formData.pincode.trim()) {
      if (!/^\d{6}$/.test(formData.pincode.trim())) {
        errors.pincode = "Pincode must be a 6-digit number";
      }
    }

    if (!formData.hire_date.trim()) {
      errors.hire_date = "Hire date is required";
    } else if (!validateDate(formData.hire_date.trim())) {
      errors.hire_date = "Please enter a valid date in DD-MM-YYYY format";
    }

    // File validation - support both image and PDF files
    const hasExistingAadhar = !!formData.aadhar_card?.trim();
    if (!aadharFile && !hasExistingAadhar) {
      errors.aadhar_card = "Aadhar card document is required";
    } else if (aadharFile) {
      if (
        !aadharFile.type.startsWith("image/") &&
        aadharFile.type !== "application/pdf"
      ) {
        errors.aadhar_card =
          "Please select a valid image or PDF file for Aadhar card";
      } else if (aadharFile.size > 5 * 1024 * 1024) {
        errors.aadhar_card = "Aadhar card document must be less than 5MB";
      }
    }

    if (panFile) {
      if (
        !panFile.type.startsWith("image/") &&
        panFile.type !== "application/pdf"
      ) {
        errors.pan_card =
          "Please select a valid image or PDF file for PAN card";
      } else if (panFile.size > 5 * 1024 * 1024) {
        errors.pan_card = "PAN card document must be less than 5MB";
      }
    }

    const hasExistingDrivingLicence = !!formData.driving_licence?.trim();
    if (!drivingLicenceFile && !hasExistingDrivingLicence) {
      errors.driving_licence = "Driving licence document is required";
    } else if (drivingLicenceFile) {
      if (
        !drivingLicenceFile.type.startsWith("image/") &&
        drivingLicenceFile.type !== "application/pdf"
      ) {
        errors.driving_licence =
          "Please select a valid image or PDF file for driving licence";
      } else if (drivingLicenceFile.size > 5 * 1024 * 1024) {
        errors.driving_licence =
          "Driving licence document must be less than 5MB";
      }
    }

    const hasExistingProfileImage = !!formData.image_url?.trim();
    if (!imageFile && !hasExistingProfileImage) {
      errors.image_url = "Profile image is required";
    } else if (imageFile) {
      if (!imageFile.type.startsWith("image/")) {
        errors.image_url = "Please select a valid image file for profile image";
      } else if (imageFile.size > 5 * 1024 * 1024) {
        errors.image_url = "Profile image must be less than 5MB";
      }
    }

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsUploading(true);

      const deliveryProfileData = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        gender: formData.gender,
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        country: formData.country.trim(),
        state: formData.state.trim(),
        city: formData.city.trim(),
        pincode: formData.pincode.trim(),
        aadhar_card: formData.aadhar_card?.trim() || "",
        pan_card: formData.pan_card?.trim() || "",
        driving_licence: formData.driving_licence?.trim() || "",
        image_url: formData.image_url?.trim() || "",
        hire_date: formData.hire_date?.trim() || "",
        is_active: formData.is_active,
      };

      // Prepare files object
      const files = {
        image: imageFile,
        aadhar_card: aadharFile,
        pan_card: panFile,
        driving_licence: drivingLicenceFile,
      };

      // Log files being sent for debugging
      console.log("=== DELIVERY PROFILE FORM SUBMISSION ===");
      console.log("Mode:", isEditMode ? "UPDATE" : "CREATE");
      console.log("Files to upload:", {
        image: imageFile
          ? { name: imageFile.name, type: imageFile.type, size: imageFile.size }
          : null,
        aadhar_card: aadharFile
          ? {
              name: aadharFile.name,
              type: aadharFile.type,
              size: aadharFile.size,
            }
          : null,
        pan_card: panFile
          ? { name: panFile.name, type: panFile.type, size: panFile.size }
          : null,
        driving_licence: drivingLicenceFile
          ? {
              name: drivingLicenceFile.name,
              type: drivingLicenceFile.type,
              size: drivingLicenceFile.size,
            }
          : null,
      });
      console.log("Profile data:", deliveryProfileData);

      if (isEditMode) {
        await dispatch(
          updateDeliveryProfile({
            id,
            profileData: deliveryProfileData,
            files,
          })
        ).unwrap();
      } else {
        await dispatch(
          createDeliveryProfile({
            profileData: deliveryProfileData,
            files,
          })
        ).unwrap();
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error(error.message || "Something went wrong");
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      first_name: "",
      last_name: "",
      gender: "",
      email: "",
      phone: "",
      address: "",
      country: "",
      state: "",
      city: "",
      pincode: "",
      aadhar_card: "",
      pan_card: "",
      driving_licence: "",
      image_url: "",
      hire_date: "",
      is_active: true,
    });
    setImageFile(null);
    setAadharFile(null);
    setPanFile(null);
    setDrivingLicenceFile(null);
    setValidationErrors({});
    navigate("/admin/delivery-profiles");
  };

  const isSubmitting = isCreating || isUpdating || isUploading;

  // Show loading state when fetching delivery profile data in edit mode
  if (isEditMode && isLoading) {
    return (
      <div className="page-container">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading delivery profile data...</p>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  // Helper function to render file preview
  const renderFilePreview = (file, existingUrl, fileType, label) => {
    const hasFile = file || existingUrl;
    if (!hasFile) return null;

    const isPdf = file
      ? file.type === "application/pdf"
      : existingUrl?.toLowerCase().includes(".pdf");
    const isImage = file ? file.type.startsWith("image/") : !isPdf;

    return (
      <div className="mt-2">
        <div className="d-flex align-items-center gap-3">
          {isImage ? (
            <img
              src={file ? URL.createObjectURL(file) : existingUrl}
              alt={`${label} Preview`}
              style={{
                width: "50px",
                height: "50px",
                objectFit: "cover",
                borderRadius: "4px",
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div
              style={{
                width: "50px",
                height: "50px",
                backgroundColor: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                color: "#dc3545",
                fontWeight: "bold",
              }}
            >
              PDF
            </div>
          )}

          <div className="flex-grow-1">
            <strong>{file ? file.name : `Current ${label}`}</strong>
            {isEditMode && !file && (
              <div className="text-muted small">Existing file will be kept</div>
            )}
          </div>

          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleRemoveFile(fileType)}
            disabled={isSubmitting}
          >
            <FaRegTrashAlt />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="page-container">
      <Container fluid>
        <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
          <h5 className="mb-0 fw-semibold text-light">
            {isEditMode
              ? "Update Delivery Profile"
              : "Create New Delivery Profile"}
          </h5>
          <div>
            <Button
              variant="success"
              className="me-2 px-4"
              size="sm"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                ? "Update"
                : "Save"}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </div>

        <Card>
          <Card.Body>
            <Form onSubmit={handleSubmit} noValidate>
              {/* Personal Information Section */}
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      First Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.first_name}
                      placeholder="Enter first name"
                      disabled={isSubmitting}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.first_name}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Last Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.last_name}
                      placeholder="Enter last name"
                      disabled={isSubmitting}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.last_name}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Gender <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.gender}
                      disabled={isSubmitting}
                    >
                      <option value="">Select gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.gender}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {/* Contact Information Section */}
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Email <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.email}
                      placeholder="Enter email"
                      disabled={isSubmitting}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.email}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Phone <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      maxLength={10}
                      isInvalid={!!validationErrors.phone}
                      placeholder="Enter phone number"
                      disabled={isSubmitting}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.phone}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Hire Date <span className="text-danger">*</span>
                    </Form.Label>
                    <div style={{ position: "relative" }}>
                      <Form.Control
                        type="text"
                        name="hire_date"
                        value={formData.hire_date}
                        onChange={(e) => {
                          const formatted = formatDateInput(e.target.value);
                          setFormData((prev) => ({
                            ...prev,
                            hire_date: formatted,
                          }));
                          // Clear validation error for this field
                          if (validationErrors.hire_date) {
                            setValidationErrors((prev) => ({
                              ...prev,
                              hire_date: "",
                            }));
                          }
                        }}
                        onBlur={(e) => {
                          // Validate on blur
                          const value = e.target.value.trim();
                          if (value && !validateDate(value)) {
                            setValidationErrors((prev) => ({
                              ...prev,
                              hire_date:
                                "Please enter a valid date in DD-MM-YYYY format",
                            }));
                          }
                        }}
                        placeholder="DD-MM-YYYY"
                        isInvalid={!!validationErrors.hire_date}
                        disabled={isSubmitting}
                        maxLength={10}
                        style={{ paddingRight: "40px" }}
                      />
                      <Button
                        variant="outline-secondary"
                        style={{
                          position: "absolute",
                          right: "5px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          border: "none",
                          background: "transparent",
                          padding: "4px 8px",
                          cursor: "pointer",
                          zIndex: 10,
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          if (dateInputRef.current) {
                            // Try modern showPicker API first
                            if (dateInputRef.current.showPicker) {
                              dateInputRef.current.showPicker();
                            } else {
                              // Fallback: click the hidden input
                              dateInputRef.current.focus();
                              dateInputRef.current.click();
                            }
                          }
                        }}
                        disabled={isSubmitting}
                        type="button"
                        title="Open calendar"
                      >
                        <FaCalendarAlt style={{ color: "#6c757d" }} />
                      </Button>
                      {/* Hidden date input for calendar picker */}
                      <input
                        ref={dateInputRef}
                        type="date"
                        style={{
                          position: "absolute",
                          opacity: 0,
                          width: "1px",
                          height: "1px",
                          pointerEvents: "none",
                          zIndex: -1,
                        }}
                        value={convertDDMMYYYYToYYYYMMDD(formData.hire_date)}
                        onChange={(e) => {
                          const selectedDate = e.target.value; // YYYY-MM-DD format from date input
                          if (selectedDate) {
                            const formattedDate =
                              convertYYYYMMDDToDDMMYYYY(selectedDate);
                            setFormData((prev) => ({
                              ...prev,
                              hire_date: formattedDate,
                            }));
                            // Clear validation error for this field
                            if (validationErrors.hire_date) {
                              setValidationErrors((prev) => ({
                                ...prev,
                                hire_date: "",
                              }));
                            }
                          } else {
                            setFormData((prev) => ({
                              ...prev,
                              hire_date: "",
                            }));
                          }
                        }}
                        max={new Date().toISOString().split("T")[0]} // Prevent future dates
                      />
                    </div>
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.hire_date}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      Format: DD-MM-YYYY (e.g., 15-01-2024) or click calendar
                      icon
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Address <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.address}
                      placeholder="Enter address"
                      disabled={isSubmitting}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.address}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Country <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="country"
                      value={selectedCountryCode}
                      onChange={handleCountryChange}
                      isInvalid={!!validationErrors.country}
                      disabled={isSubmitting}
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country.isoCode} value={country.isoCode}>
                          {country.name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.country}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      State <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="state"
                      value={selectedStateCode}
                      onChange={handleStateChange}
                      isInvalid={!!validationErrors.state}
                      disabled={isSubmitting || !selectedCountryCode}
                    >
                      <option value="">
                        {selectedCountryCode
                          ? "Select State"
                          : "Select Country First"}
                      </option>
                      {states.map((state) => (
                        <option key={state.isoCode} value={state.isoCode}>
                          {state.name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.state}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      City <span className="text-danger">*</span>
                    </Form.Label>
                    {selectedStateCode && cities.length > 0 ? (
                      <Form.Select
                        name="city"
                        value={formData.city}
                        onChange={handleCityChange}
                        isInvalid={!!validationErrors.city}
                        disabled={isSubmitting}
                      >
                        <option value="">Select City</option>
                        {cities.map((city, index) => (
                          <option
                            key={`${city.name}-${index}`}
                            value={city.name}
                          >
                            {city.name}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <Form.Control
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        isInvalid={!!validationErrors.city}
                        placeholder={
                          selectedStateCode
                            ? "Enter city name"
                            : "Select State First"
                        }
                        disabled={isSubmitting || !selectedStateCode}
                      />
                    )}
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.city}
                    </Form.Control.Feedback>
                    {selectedStateCode && cities.length === 0 && (
                      <Form.Text className="text-muted">
                        City list not available. Please enter city name
                        manually.
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Pincode</Form.Label>
                    <Form.Control
                      type="text"
                      name="pincode"
                      maxLength={6}
                      value={formData.pincode}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.pincode}
                      placeholder="Enter 6-digit pincode"
                      disabled={isSubmitting}
                      pattern="[0-9]{6}"
                      inputMode="numeric"
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.pincode}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      name="is_active"
                      value={formData.is_active}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                    >
                      <option value={true}>Active</option>
                      <option value={false}>Inactive</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* Document Upload Section */}
              <Row>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>
                      Aadhar Card <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="file"
                      id="file-uplad"
                      accept="image/*,.pdf"
                      onChange={(e) => handleImageSelect(e, "aadhar")}
                      disabled={isSubmitting}
                      isInvalid={!!validationErrors.aadhar_card}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.aadhar_card}
                    </Form.Control.Feedback>
                    {renderFilePreview(
                      aadharFile,
                      formData.aadhar_card,
                      "aadhar",
                      "Aadhar Card"
                    )}
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group>
                    <Form.Label>PAN Card</Form.Label>
                    <Form.Control
                      type="file"
                      id="file-uplad"
                      accept="image/*,.pdf"
                      onChange={(e) => handleImageSelect(e, "pan")}
                      disabled={isSubmitting}
                      isInvalid={!!validationErrors.pan_card}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.pan_card}
                    </Form.Control.Feedback>
                    {renderFilePreview(
                      panFile,
                      formData.pan_card,
                      "pan",
                      "PAN Card"
                    )}
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group>
                    <Form.Label>
                      Driving Licence <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="file"
                      id="file-uplad"
                      accept="image/*,.pdf"
                      onChange={(e) => handleImageSelect(e, "driving_licence")}
                      disabled={isSubmitting}
                      isInvalid={!!validationErrors.driving_licence}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.driving_licence}
                    </Form.Control.Feedback>
                    {renderFilePreview(
                      drivingLicenceFile,
                      formData.driving_licence,
                      "driving_licence",
                      "Driving Licence"
                    )}
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group>
                    <Form.Label>
                      Profile Image <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="file"
                      id="file-uplad"
                      accept="image/*"
                      onChange={(e) => handleImageSelect(e, "profile")}
                      disabled={isSubmitting}
                      isInvalid={!!validationErrors.image_url}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.image_url}
                    </Form.Control.Feedback>
                    {renderFilePreview(
                      imageFile,
                      formData.image_url,
                      "profile",
                      "Profile Image"
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default DeliveryProfileForm;
